import React, { useEffect, useState, useRef } from "react";
import classes from './AssetMaintainance.module.css';
import HeaderNav from '../../Components/Dahboard Header/HeaderNav/HeaderNav';
import Footer from "../../Components/Footer/Footer";
import { useLocation, useNavigate } from 'react-router-dom';
import { Eye, Pencil, Trash2, X, Wrench } from 'lucide-react';
import searchicon from "../../Assets/searchicon.png";
import filter from "../../Assets/filter.png";
import axios from "axios";
import { BASE_URL } from "../../Components/API/Api";
import Swal from 'sweetalert2';

const AssetMaintenance = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [isActive, setIsActive] = useState(false);
    const [selectedMetrics, setSelectedMetrics] = useState({
        total_maintenance: 0,
        total_cost: 0,
        upcoming_service: 0,
        overdue_service: 0,
    });

    const [loading, setLoading] = useState(true);
    const [modalLoading, setModalLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [assetMaintenance, setAssetMaintenance] = useState([]);
    const [companyId, setCompanyId] = useState(null);

    // Modal state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showViewEditModal, setShowViewEditModal] = useState(false);
    const [modalMode, setModalMode] = useState("view");
    const [selectedMaintenance, setSelectedMaintenance] = useState(null);

    // Create Modal state
    const [assets, setAssets] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [createFormData, setCreateFormData] = useState({
        asset_id: "",
        service_date: "",
        description: "",
        cost: "",
        vendor_id: "",
        next_service_date: "",
        company_id: ""
    });
    const [createLoading, setCreateLoading] = useState({
        assets: false,
        vendors: false
    });
    const [submitting, setSubmitting] = useState(false);

    // For searchable selects in create modal
    const [searchTerms, setSearchTerms] = useState({
        asset: "",
        vendor: ""
    });
    const [showDropdowns, setShowDropdowns] = useState({
        asset: false,
        vendor: false
    });

    // Create refs for each dropdown
    const assetRef = useRef(null);
    const vendorRef = useRef(null);

    // Modal form data for view/edit
    const [modalData, setModalData] = useState({
        id: null,
        asset_name: "",
        asset_code: "",
        service_date: "",
        description: "",
        cost: "",
        vendor_name: "",
        vendor_id: "",
        next_service_date: "",
        status: "",
        company_id: ""
    });

    // Helper function to get company ID from any source
    const getCompanyIdFromStorage = () => {
        // Try direct company_id
        const directId = localStorage.getItem("company_id");
        if (directId) return directId;

        // Try companyInfo
        const companyInfo = localStorage.getItem("companyInfo");
        if (companyInfo) {
            try {
                const parsed = JSON.parse(companyInfo);
                const id = parsed?.id || parsed?.company_id || parsed?.companyId;
                if (id) return String(id);
            } catch (e) {
                console.error("Error parsing companyInfo:", e);
            }
        }

        // Try user data
        const userData = localStorage.getItem("user");
        if (userData) {
            try {
                const parsed = JSON.parse(userData);

                // Direct company_id
                if (parsed.company_id) return String(parsed.company_id);

                // Company object
                if (parsed.company) {
                    const id = parsed.company?.id || parsed.company?.company_id || parsed.company?.companyId;
                    if (id) return String(id);
                }

                // Companies array
                if (parsed.companies?.length > 0) {
                    // Try main company first
                    const mainCompany = parsed.companies.find(c => c.is_main === "1" || c.is_main === 1);
                    if (mainCompany) {
                        const id = mainCompany?.id || mainCompany?.company_id || mainCompany?.companyId;
                        if (id) return String(id);
                    }

                    // Use first company
                    const firstCompany = parsed.companies[0];
                    const id = firstCompany?.id || firstCompany?.company_id || firstCompany?.companyId;
                    if (id) return String(id);
                }
            } catch (e) {
                console.error("Error parsing user data:", e);
            }
        }

        return null;
    };

    // Function to get valid company ID (from state or storage)
    const getValidCompanyId = () => {
        // First try from state
        if (companyId) return companyId;

        // Then try from storage
        const storageId = getCompanyIdFromStorage();
        if (storageId) {
            // Update state for future use
            setCompanyId(storageId);
            setCreateFormData(prev => ({
                ...prev,
                company_id: storageId
            }));
            return storageId;
        }

        return null;
    };

    // Initialize company ID on mount
    useEffect(() => {
        const id = getCompanyIdFromStorage();
        if (id) {
            console.log("Found company ID:", id);
            setCompanyId(id);
            setCreateFormData(prev => ({
                ...prev,
                company_id: id
            }));
        } else {
            console.warn("No company ID found");
            Swal.fire({
                icon: 'warning',
                title: 'Company ID Missing',
                text: 'Please log in again or select a company.',
                confirmButtonText: 'Go to Login'
            }).then(() => {
                navigate('/login');
            });
        }
    }, [navigate]);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (assetRef.current && !assetRef.current.contains(event.target)) {
                setShowDropdowns(prev => ({ ...prev, asset: false }));
            }
            if (vendorRef.current && !vendorRef.current.contains(event.target)) {
                setShowDropdowns(prev => ({ ...prev, vendor: false }));
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Fetch data for create modal when opened
    useEffect(() => {
        if (showCreateModal) {
            const validCompanyId = getValidCompanyId();
            if (validCompanyId) {
                fetchAssets();
                fetchVendors();
            } else {
                Swal.fire({
                    icon: 'warning',
                    title: 'Company ID Missing',
                    text: 'Please log in again.',
                    confirmButtonText: 'Go to Login'
                }).then(() => {
                    setShowCreateModal(false);
                    navigate('/login');
                });
            }
        }
    }, [showCreateModal]);

    // Fetch asset maintenance when companyId is available
    useEffect(() => {
        if (companyId) {
            fetchAssetMaintenance();
        }
    }, [companyId]);

    const fetchAssetMaintenance = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("authToken");

            if (!token) {
                Swal.fire({
                    icon: 'error',
                    title: 'Authentication Error',
                    text: 'Please log in again'
                });
                setLoading(false);
                return;
            }

            // Get valid company ID
            const validCompanyId = getValidCompanyId();

            if (!validCompanyId) {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Company ID not found. Please log in again.'
                });
                setLoading(false);
                return;
            }

            console.log(`Fetching asset maintenance for company ID: ${validCompanyId}`);

            const response = await axios.get(`${BASE_URL}/v1/assetmaintenance`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                params: {
                    company_id: validCompanyId
                }
            });

            console.log("Asset maintenance response:", response.data);

            if (response.data.status && Array.isArray(response.data.data)) {
                setAssetMaintenance(response.data.data);

                // Update metrics
                const totalCost = response.data.data.reduce((sum, item) => sum + (parseFloat(item.cost) || 0), 0);

                // Count upcoming and overdue services
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                let upcomingCount = 0;
                let overdueCount = 0;

                response.data.data.forEach(item => {
                    if (item.next_service_date) {
                        const nextDate = new Date(item.next_service_date);
                        nextDate.setHours(0, 0, 0, 0);

                        const diffTime = nextDate - today;
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                        if (diffDays > 0 && diffDays <= 30) {
                            upcomingCount++;
                        } else if (diffDays < 0) {
                            overdueCount++;
                        }
                    }
                });

                setSelectedMetrics({
                    total_maintenance: response.data.data.length,
                    total_cost: totalCost,
                    upcoming_service: upcomingCount,
                    overdue_service: overdueCount
                });
            } else {
                setAssetMaintenance([]);
            }
        } catch (error) {
            console.error("Error fetching asset maintenance:", error);

            let errorMessage = "Failed to fetch asset maintenance";
            if (error.response?.data?.message) {
                errorMessage = typeof error.response.data.message === 'object'
                    ? Object.values(error.response.data.message).flat().join(', ')
                    : error.response.data.message;
            } else if (error.response?.status === 401) {
                errorMessage = "Unauthorized. Please log in again.";
            } else if (error.response?.status === 404) {
                errorMessage = "API endpoint not found";
            } else if (error.request) {
                errorMessage = "No response from server. Please check your connection.";
            }

            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: errorMessage
            });

            setAssetMaintenance([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchAssets = async () => {
        setCreateLoading(prev => ({ ...prev, assets: true }));
        try {
            const token = localStorage.getItem("authToken");

            // Get valid company ID
            const validCompanyId = getValidCompanyId();

            if (!validCompanyId) {
                console.error("Cannot fetch assets: No company ID");
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Company ID not found. Please try again.'
                });
                return;
            }

            const response = await axios.get(`${BASE_URL}/v1/fixedassets/fetch`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { company_id: validCompanyId }
            });

            if (response.data.status && Array.isArray(response.data.data)) {
                setAssets(response.data.data);
            } else {
                setAssets([]);
            }
        } catch (error) {
            console.error("Error fetching assets:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch assets. Please try again.'
            });
        } finally {
            setCreateLoading(prev => ({ ...prev, assets: false }));
        }
    };

    const fetchVendors = async () => {
        setCreateLoading(prev => ({ ...prev, vendors: true }));
        try {
            const token = localStorage.getItem("authToken");

            const response = await axios.get(`${BASE_URL}/v1/beneficiary/get`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (Array.isArray(response.data?.data)) {
                setVendors(response.data.data);
            } else {
                setVendors([]);
            }
        } catch (error) {
            console.error("Error fetching vendors:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch vendors. Please try again.'
            });
        } finally {
            setCreateLoading(prev => ({ ...prev, vendors: false }));
        }
    };

    const pageMap = {
        '/dashboard': 'Dashboard',
        '/transactions': 'Transactions',
        '/accounts': 'Accounting',
        '/reports': 'Reports',
        '/banking': 'Banking',
        '/settings': 'Settings',
        '/fixed-assets': 'Fixed Assets',
    };

    // Process maintenance data for modal
    const processMaintenanceData = (maintenance) => {
        let status = "Completed";
        if (maintenance.next_service_date) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const nextDate = new Date(maintenance.next_service_date);
            nextDate.setHours(0, 0, 0, 0);

            const diffTime = nextDate - today;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            if (diffDays < 0) {
                status = "Overdue";
            } else if (diffDays <= 30) {
                status = "Upcoming";
            } else {
                status = "Scheduled";
            }
        }

        const validCompanyId = getValidCompanyId();

        setModalData({
            id: maintenance.id,
            asset_name: maintenance.fixedasset?.asset_name || "N/A",
            asset_code: maintenance.fixedasset?.asset_code || "N/A",
            service_date: maintenance.service_date || "",
            description: maintenance.description || "",
            cost: maintenance.cost || "",
            vendor_name: maintenance.vendor?.name || maintenance.vendor_name || "N/A",
            vendor_id: maintenance.vendor_id || "",
            next_service_date: maintenance.next_service_date || "",
            status: status,
            company_id: validCompanyId || maintenance.company_id || ""
        });
    };

    const handleViewClick = (maintenance) => {
        setModalMode("view");
        setSelectedMaintenance(maintenance);
        setModalLoading(true);
        setShowViewEditModal(true);
        processMaintenanceData(maintenance);
        setModalLoading(false);
    };

    const handleEditClick = (maintenance) => {
        setModalMode("edit");
        setSelectedMaintenance(maintenance);
        setModalLoading(true);
        setShowViewEditModal(true);
        processMaintenanceData(maintenance);
        setModalLoading(false);
    };

    const handleModalChange = (field, value) => {
        setModalData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleCreateChange = (e) => {
        const { name, value } = e.target;
        setCreateFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSearchChange = (field, e) => {
        setSearchTerms(prev => ({ ...prev, [field]: e.target.value }));
        if (!showDropdowns[field]) {
            setShowDropdowns(prev => ({ ...prev, [field]: true }));
        }
    };

    const handleSelect = (field, item) => {
        if (field === 'asset') {
            setCreateFormData(prev => ({
                ...prev,
                asset_id: item.id
            }));

            const displayName = `${item.asset_name} (${item.asset_code})`;
            setSearchTerms(prev => ({ ...prev, asset: displayName }));
            setShowDropdowns(prev => ({ ...prev, asset: false }));
        } else if (field === 'vendor') {
            setCreateFormData(prev => ({
                ...prev,
                vendor_id: item.id
            }));

            setSearchTerms(prev => ({ ...prev, vendor: item.name || item.vendor_name || "" }));
            setShowDropdowns(prev => ({ ...prev, vendor: false }));
        }
    };

    const getSelectedName = (field) => {
        if (field === 'asset') {
            const id = createFormData.asset_id;
            if (!id) return "";

            const item = assets.find(i => i.id === id);
            if (!item) return "";

            return `${item.asset_name} (${item.asset_code})`;
        } else if (field === 'vendor') {
            const id = createFormData.vendor_id;
            if (!id) return "";

            const item = vendors.find(i => i.id === id);
            if (!item) return "";

            return item.name || item.vendor_name || "";
        }
        return "";
    };

    const filterItems = (field) => {
        const searchTerm = (searchTerms[field] || "").toLowerCase();

        if (field === 'asset') {
            if (!Array.isArray(assets)) return [];

            return assets.filter(item => {
                if (!item) return false;

                const assetName = (item.asset_name || "").toLowerCase();
                const assetCode = (item.asset_code || "").toLowerCase();
                return assetName.includes(searchTerm) || assetCode.includes(searchTerm);
            });
        } else if (field === 'vendor') {
            if (!Array.isArray(vendors)) return [];

            return vendors.filter(item => {
                if (!item) return false;

                const name = (item.name || item.vendor_name || "").toLowerCase();
                return name.includes(searchTerm);
            });
        }
        return [];
    };

    const handleSave = async () => {
        if (modalMode === "edit") {
            setSaveLoading(true);
            try {
                const token = localStorage.getItem("authToken");

                // Get valid company ID
                const validCompanyId = getValidCompanyId();

                if (!validCompanyId) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Company ID Error',
                        text: 'Company ID not found. Please log in again.'
                    });
                    setSaveLoading(false);
                    return;
                }

                const requestData = {
                    service_date: modalData.service_date,
                    description: modalData.description,
                    cost: modalData.cost,
                    vendor_id: modalData.vendor_id || null,
                    next_service_date: modalData.next_service_date || null,
                    company_id: validCompanyId
                };

                console.log("Updating asset maintenance with data:", requestData);

                const response = await axios.post(
                    `${BASE_URL}/v1/assetmaintenance/update/${selectedMaintenance.id}`,
                    requestData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        },
                    }
                );

                console.log("Update response:", response.data);

                if (response.data.status) {
                    await fetchAssetMaintenance();
                    setShowViewEditModal(false);

                    Swal.fire({
                        icon: 'success',
                        title: 'Success!',
                        text: response.data.message || 'Asset maintenance updated successfully!',
                        timer: 3000,
                        showConfirmButton: false
                    });
                } else {
                    let errorMessage = "Failed to update asset maintenance";
                    if (response.data.message) {
                        errorMessage = typeof response.data.message === 'object'
                            ? Object.values(response.data.message).flat().join(', ')
                            : response.data.message;
                    }

                    Swal.fire({
                        icon: 'error',
                        title: 'Error!',
                        text: errorMessage
                    });
                }
            } catch (error) {
                console.error("Error updating asset maintenance:", error);

                let errorMessage = "Failed to update asset maintenance";
                if (error.response?.data?.message) {
                    errorMessage = typeof error.response.data.message === 'object'
                        ? Object.values(error.response.data.message).flat().join(', ')
                        : error.response.data.message;
                } else if (error.request) {
                    errorMessage = "No response from server. Please check your connection.";
                }

                Swal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: errorMessage,
                    confirmButtonText: 'OK'
                });
            } finally {
                setSaveLoading(false);
            }
        } else {
            setShowViewEditModal(false);
        }
    };

    const handleCreateSubmit = async (e) => {
        e.preventDefault();

        if (!createFormData.asset_id || !createFormData.service_date || !createFormData.cost) {
            Swal.fire("Error", "Please fill in all required fields", "error");
            return;
        }

        // Get valid company ID
        const validCompanyId = getValidCompanyId();

        if (!validCompanyId) {
            Swal.fire({
                icon: 'error',
                title: 'Company ID Error',
                text: 'Company ID not found. Please log in again.',
                confirmButtonText: 'Go to Login'
            }).then(() => {
                navigate('/login');
            });
            return;
        }

        try {
            setSubmitting(true);
            const token = localStorage.getItem("authToken");

            const payload = {
                asset_id: createFormData.asset_id,
                service_date: createFormData.service_date,
                description: createFormData.description || null,
                cost: createFormData.cost,
                vendor_id: createFormData.vendor_id || null,
                next_service_date: createFormData.next_service_date || null,
                company_id: validCompanyId
            };

            console.log("Creating asset maintenance with payload:", payload);

            const response = await axios.post(
                `${BASE_URL}/v1/assetmaintenance/create`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            console.log("Create response:", response.data);

            if (response.data?.status) {
                Swal.fire({
                    title: "Success",
                    text: response.data.message || "Asset maintenance recorded successfully",
                    icon: "success",
                    timer: 1500,
                    showConfirmButton: false
                });

                resetCreateForm();
                onClose();
                await fetchAssetMaintenance();
            } else {
                let errorMessage = "Failed to record asset maintenance";
                if (response.data?.message) {
                    errorMessage = typeof response.data.message === 'object'
                        ? Object.values(response.data.message).flat().join(', ')
                        : response.data.message;
                }
                Swal.fire("Error", errorMessage, "error");
            }
        } catch (error) {
            console.error("Error creating asset maintenance:", error);

            let errorMessage = "Something went wrong while recording the maintenance";
            if (error.response?.data?.message) {
                errorMessage = typeof error.response.data.message === 'object'
                    ? Object.values(error.response.data.message).flat().join(', ')
                    : error.response.data.message;
            } else if (error.response?.status === 422) {
                errorMessage = "Validation error. Please check all fields.";
            }

            Swal.fire("Error", errorMessage, "error");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = async (maintenance) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem("authToken");

                // Get valid company ID
                const validCompanyId = getValidCompanyId();

                if (!validCompanyId) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: 'Company ID not found. Please try again.'
                    });
                    return;
                }

                console.log(`Deleting asset maintenance with ID: ${maintenance.id} for company: ${validCompanyId}`);

                const response = await axios.delete(
                    `${BASE_URL}/v1/assetmaintenance/${maintenance.id}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                        params: {
                            company_id: validCompanyId
                        }
                    }
                );

                console.log("Delete response:", response.data);

                if (response.data.status) {
                    await fetchAssetMaintenance();

                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: response.data.message || 'Asset maintenance deleted successfully!',
                        timer: 3000,
                        showConfirmButton: false
                    });
                } else {
                    throw new Error(response.data.message || "Failed to delete asset maintenance");
                }
            } catch (error) {
                console.error("Error deleting asset maintenance:", error);

                let errorMessage = "Failed to delete asset maintenance";
                if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                }

                Swal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: errorMessage,
                    confirmButtonText: 'OK'
                });
            }
        }
    };

    const resetCreateForm = () => {
        const validCompanyId = getValidCompanyId();
        setCreateFormData({
            asset_id: "",
            service_date: "",
            description: "",
            cost: "",
            vendor_id: "",
            next_service_date: "",
            company_id: validCompanyId || ""
        });
        setSearchTerms({
            asset: "",
            vendor: ""
        });
    };

    const onClose = () => {
        setShowCreateModal(false);
        resetCreateForm();
        setShowDropdowns({
            asset: false,
            vendor: false
        });
    };

    const formatCurrency = (value) => {
        if (!value) return "0.00";
        return parseFloat(value).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const formatDate = (date) => {
        if (!date) return "—";
        return new Date(date).toLocaleDateString();
    };

    const getStatusClass = (status) => {
        switch (status) {
            case 'Overdue': return classes.overdueBadge;
            case 'Upcoming': return classes.upcomingBadge;
            case 'Scheduled': return classes.scheduledBadge;
            default: return classes.completedBadge;
        }
    };

    const TableSkeleton = () => {
        return Array.from({ length: 5 }).map((_, index) => (
            <tr key={index} className={classes.skeletonRow}>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td>
                    <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                        <div className={classes.skeletonIcon}></div>
                        <div className={classes.skeletonIcon}></div>
                        <div className={classes.skeletonIcon}></div>
                    </div>
                </td>
            </tr>
        ));
    };

    const ModalInputSkeleton = () => (
        <div className={classes.skeletonInput}></div>
    );

    return (
        <div>
            <HeaderNav activePage={pageMap[location.pathname] || "Fixed Assets"} />

            <div className={classes.Container}>
                <div className={classes.Container}>
                    <div className={classes.actions}>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/assets')}>
                            Assets
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/categories')}>
                            Categories
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/locations')}>
                            Locations
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/assignments')}>
                            Assignments
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/depreciation')}>
                            Depreciation
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/revaluation')}>
                            Revaluation
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/disposal')}>
                            Disposal
                        </button>
                        <button className={`${classes.btns} ${isActive ? classes.active : ""}`} onClick={() => setIsActive(true)}>
                            Maintenance
                        </button>
                    </div>

                    <header className={classes.Header}>
                        <h2 className={classes.Title}>Asset Maintenance</h2>
                    </header>

                    <div className={classes.entrjj}>
                        <div className={classes.allkads}>
                            <div className={classes.thrdkad}>
                                <div className={classes.kad}>
                                    <p className={classes.kdptg}>Total Records</p>
                                    <p className={classes.kdptg1}>
                                        {selectedMetrics?.total_maintenance?.toLocaleString() ?? '0'}
                                    </p>
                                    <div className={classes.rvnue}>
                                        <p className={classes.skaltxt}>Maintenance entries</p>
                                    </div>
                                </div>
                            </div>
                            <div className={classes.frtkad}>
                                <div className={classes.kad}>
                                    <p className={classes.kdptg}>Total Cost</p>
                                    <p className={classes.kdptg2}>
                                        {formatCurrency(selectedMetrics?.total_cost)}
                                    </p>
                                    <div className={classes.rvnue}>
                                        <p className={classes.skaltxt}>Maintenance expenses</p>
                                    </div>
                                </div>
                            </div>
                            <div className={classes.fiftkad}>
                                <div className={classes.kad}>
                                    <p className={classes.kdptg}>Upcoming</p>
                                    <p className={classes.kdptg3} >
                                        {selectedMetrics?.upcoming_service}
                                    </p>
                                    <div className={classes.rvnue}>
                                        <p className={classes.skaltxt}>Next 30 days</p>
                                    </div>
                                </div>
                            </div>
                            <div className={classes.frstkad}>
                                <div className={classes.kad}>
                                    <p className={classes.kdptg}>Overdue</p>
                                    <p className={classes.kdptg4}>
                                        {selectedMetrics?.overdue_service}
                                    </p>
                                    <div className={classes.rvnue}>
                                        <p className={classes.skaltxt}>Past due date</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={classes.headerActions}>
                        <div className={classes.actionsRight}>
                            <div className={classes.inputWrapper}>
                                <img src={searchicon} alt="Search" className={classes.inputIconGreen} />
                                <input type="text" className={classes.searchBar} placeholder="Search maintenance" />
                            </div>
                            <div className={classes.selectWrapper}>
                                <select className={classes.dropdown}>
                                    <option value="">All Status</option>
                                    <option value="completed">Completed</option>
                                    <option value="scheduled">Scheduled</option>
                                    <option value="upcoming">Upcoming</option>
                                    <option value="overdue">Overdue</option>
                                </select>
                                <span className={classes.dropdownArrow}>▼</span>
                            </div>
                            <div className={classes.inputWrapper}>
                                <input type="text" className={classes.searchBars} placeholder="Filter..." />
                                <img src={filter} alt="Filter" className={classes.inputIcon} />
                            </div>
                        </div>

                        <button className={classes.btnss} onClick={() => setShowCreateModal(true)}>
                            + Record Maintenance
                        </button>
                    </div>

                    <table className={classes.invoiceTable}>
                        <thead>
                            <tr>
                                <th>S/N</th>
                                <th>Asset</th>
                                <th>Service Date</th>
                                <th>Description</th>
                                <th>Cost</th>
                                <th>Vendor</th>
                                <th>Next Service</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <TableSkeleton />
                            ) : assetMaintenance.length > 0 ? (
                                assetMaintenance.map((maintenance, index) => {
                                    // Determine status for display
                                    let status = "Completed";
                                    if (maintenance.next_service_date) {
                                        const today = new Date();
                                        today.setHours(0, 0, 0, 0);
                                        const nextDate = new Date(maintenance.next_service_date);
                                        nextDate.setHours(0, 0, 0, 0);

                                        const diffTime = nextDate - today;
                                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                                        if (diffDays < 0) {
                                            status = "Overdue";
                                        } else if (diffDays <= 30) {
                                            status = "Upcoming";
                                        } else {
                                            status = "Scheduled";
                                        }
                                    }

                                    return (
                                        <tr key={maintenance.id}>
                                            <td>{index + 1}</td>
                                            <td>
                                                {maintenance.fixedasset?.asset_name || "—"}
                                                {maintenance.asset?.asset_code && <div className={classes.smallText}>{maintenance.asset.asset_code}</div>}
                                            </td>
                                            <td>{formatDate(maintenance.service_date)}</td>
                                            <td>
                                                {maintenance.description ? (
                                                    <div className={classes.descriptionCell} title={maintenance.description}>
                                                        {maintenance.description.length > 30
                                                            ? `${maintenance.description.substring(0, 30)}...`
                                                            : maintenance.description}
                                                    </div>
                                                ) : "—"}
                                            </td>
                                            <td style={{ textAlign: 'right' }} className={classes.amount}>{formatCurrency(maintenance.cost)}</td>
                                            <td>{maintenance.vendor?.name || maintenance.vendor_name || "—"}</td>
                                            <td>{formatDate(maintenance.next_service_date)}</td>
                                            <td>
                                                <span className={getStatusClass(status)}>
                                                    {status}
                                                </span>
                                            </td>
                                            <td>
                                                <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                                                    <Eye
                                                        size={16}
                                                        style={{ cursor: "pointer", color: "#007bff" }}
                                                        onClick={() => handleViewClick(maintenance)}
                                                    />
                                                    <Pencil
                                                        size={16}
                                                        style={{ cursor: "pointer", color: "#28a745" }}
                                                        onClick={() => handleEditClick(maintenance)}
                                                    />
                                                    <Trash2
                                                        size={16}
                                                        style={{ cursor: "pointer", color: "#dc3545" }}
                                                        onClick={() => handleDeleteClick(maintenance)}
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="9" style={{ textAlign: "center", padding: "20px" }}>
                                        {loading ? "Loading..." : "No asset maintenance records found"}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* View/Edit Modal */}
            {showViewEditModal && (
                <div className={classes.overlay}>
                    <div className={classes.modal}>
                        <div className={classes.header}>
                            <div className={classes.title}>
                                <Wrench className={classes.icon} />
                                <h2>{modalMode === "edit" ? "Edit Maintenance Record" : "View Maintenance Record"}</h2>
                            </div>
                            <button className={classes.closeBtn} onClick={() => setShowViewEditModal(false)} disabled={saveLoading}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className={classes.formGrid}>
                            {modalLoading ? (
                                Array.from({ length: 7 }).map((_, index) => (
                                    <div className={classes.formGroup} key={index}>
                                        <div className={classes.skeletonLabel}></div>
                                        <ModalInputSkeleton />
                                    </div>
                                ))
                            ) : (
                                <>
                                    <div className={classes.formGroup}>
                                        <label>Asset</label>
                                        <input
                                            type="text"
                                            value={`${modalData.asset_name} (${modalData.asset_code})`}
                                            readOnly
                                            className={classes.Field2}
                                        />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Service Date</label>
                                        <input
                                            type={modalMode === "edit" ? "date" : "text"}
                                            value={modalData.service_date}
                                            readOnly={modalMode === "view" || saveLoading}
                                            onChange={(e) => handleModalChange("service_date", e.target.value)}
                                            className={classes.Field2}
                                        />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Description</label>
                                        <textarea
                                            value={modalData.description}
                                            readOnly={modalMode === "view" || saveLoading}
                                            onChange={(e) => handleModalChange("description", e.target.value)}
                                            className={classes.textarea2}
                                            rows="3"
                                            placeholder="Enter maintenance description"
                                        />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Cost</label>
                                        <input
                                            type="number"
                                            value={modalData.cost}
                                            readOnly={modalMode === "view" || saveLoading}
                                            onChange={(e) => handleModalChange("cost", e.target.value)}
                                            className={modalMode === "edit" ? classes.number2 : classes.Field2}
                                            min="0"
                                            step="0.01"
                                            style={{ textAlign: 'right' }}
                                        />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Vendor</label>
                                        {modalMode === "edit" ? (
                                            <div className={classes.searchSelectWrapper} ref={vendorRef}>
                                                <div className={classes.searchInputContainer}>
                                                    <input
                                                        type="text"
                                                        className={classes.searchSelectInput}
                                                        placeholder="Search and select vendor..."
                                                        value={showDropdowns.vendor ? searchTerms.vendor : (modalData.vendor_name !== "N/A" ? modalData.vendor_name : "")}
                                                        onChange={(e) => handleSearchChange('vendor', e)}
                                                        onClick={() => setShowDropdowns(prev => ({ ...prev, vendor: true }))}
                                                        onFocus={() => setShowDropdowns(prev => ({ ...prev, vendor: true }))}
                                                        disabled={saveLoading}
                                                    />
                                                </div>

                                                {showDropdowns.vendor && (
                                                    <div className={classes.searchDropdown}>
                                                        <div className={classes.dropdownList}>
                                                            {createLoading.vendors ? (
                                                                <div className={classes.dropdownItem}>Loading vendors...</div>
                                                            ) : filterItems('vendor').length === 0 ? (
                                                                <div className={classes.dropdownItem}>No vendors found</div>
                                                            ) : (
                                                                filterItems('vendor').map((item) => (
                                                                    <div
                                                                        key={item.id}
                                                                        className={`${classes.dropdownItem} ${modalData.vendor_id === item.id ? classes.selectedItem : ''}`}
                                                                        onClick={() => {
                                                                            handleSelect('vendor', item);
                                                                            handleModalChange('vendor_id', item.id);
                                                                            handleModalChange('vendor_name', item.name || item.vendor_name);
                                                                        }}
                                                                    >
                                                                        {item.name || item.vendor_name}
                                                                    </div>
                                                                ))
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <input
                                                type="text"
                                                value={modalData.vendor_name}
                                                readOnly
                                                className={classes.Field2}
                                            />
                                        )}
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Next Service Date</label>
                                        <input
                                            type={modalMode === "edit" ? "date" : "text"}
                                            value={modalData.next_service_date}
                                            readOnly={modalMode === "view" || saveLoading}
                                            onChange={(e) => handleModalChange("next_service_date", e.target.value)}
                                            className={classes.Field2}
                                            min={modalData.service_date}
                                        />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Status</label>
                                        <input
                                            type="text"
                                            value={modalData.status}
                                            readOnly
                                            className={classes.Field2}
                                        />
                                    </div>
                                </>
                            )}
                        </div>

                        <button
                            className={classes.saveBtn}
                            onClick={handleSave}
                            disabled={saveLoading}
                        >
                            {saveLoading ? (
                                <span className={classes.loadingText}>
                                    <span className={classes.spinner}></span>
                                    Saving...
                                </span>
                            ) : (
                                modalMode === "edit" ? "Update Record" : "Close"
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Create Modal */}
            {showCreateModal && (
                <div className={classes.overlay}>
                    <div className={classes.modal}>
                        <div className={classes.header}>
                            <div className={classes.title}>
                                <Wrench className={classes.icon} />
                                <h2>Record Asset Maintenance</h2>
                            </div>
                            <button className={classes.closeBtn} onClick={onClose}>
                                <X size={18} />
                            </button>
                        </div>

                        <form onSubmit={handleCreateSubmit}>
                            <div className={classes.formGrid}>
                                <div className={classes.formGroup}>
                                    <label>Select Asset *</label>
                                    <div className={classes.searchSelectWrapper} ref={assetRef}>
                                        <div className={classes.searchInputContainer}>
                                            <input
                                                type="text"
                                                className={classes.searchSelectInput}
                                                placeholder="Search and select asset..."
                                                value={showDropdowns.asset ? searchTerms.asset : getSelectedName('asset')}
                                                onChange={(e) => handleSearchChange('asset', e)}
                                                onClick={() => setShowDropdowns(prev => ({ ...prev, asset: true }))}
                                                onFocus={() => setShowDropdowns(prev => ({ ...prev, asset: true }))}
                                                required
                                            />
                                        </div>

                                        {showDropdowns.asset && (
                                            <div className={classes.searchDropdown}>
                                                <div className={classes.dropdownList}>
                                                    {createLoading.assets ? (
                                                        <div className={classes.dropdownItem}>Loading assets...</div>
                                                    ) : filterItems('asset').length === 0 ? (
                                                        <div className={classes.dropdownItem}>No assets found</div>
                                                    ) : (
                                                        filterItems('asset').map((item) => (
                                                            <div
                                                                key={item.id}
                                                                className={`${classes.dropdownItem} ${createFormData.asset_id === item.id ? classes.selectedItem : ''}`}
                                                                onClick={() => handleSelect('asset', item)}
                                                            >
                                                                {item.asset_name} ({item.asset_code})
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Service Date *</label>
                                    <input
                                        type="date"
                                        name="service_date"
                                        value={createFormData.service_date}
                                        onChange={handleCreateChange}
                                        required
                                    />
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Description</label>
                                    <textarea
                                        name="description"
                                        placeholder="Enter maintenance description"
                                        value={createFormData.description}
                                        onChange={handleCreateChange}
                                        rows="3"
                                    />
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Cost *</label>
                                    <input
                                        type="number"
                                        name="cost"
                                        placeholder="0.00"
                                        value={createFormData.cost}
                                        onChange={handleCreateChange}
                                        min="0"
                                        step="0.01"
                                        required
                                        style={{ textAlign: 'right' }}
                                    />
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Vendor (Optional)</label>
                                    <div className={classes.searchSelectWrapper} ref={vendorRef}>
                                        <div className={classes.searchInputContainer}>
                                            <input
                                                type="text"
                                                className={classes.searchSelectInput}
                                                placeholder="Search and select vendor..."
                                                value={showDropdowns.vendor ? searchTerms.vendor : getSelectedName('vendor')}
                                                onChange={(e) => handleSearchChange('vendor', e)}
                                                onClick={() => setShowDropdowns(prev => ({ ...prev, vendor: true }))}
                                                onFocus={() => setShowDropdowns(prev => ({ ...prev, vendor: true }))}
                                            />
                                        </div>

                                        {showDropdowns.vendor && (
                                            <div className={classes.searchDropdown}>
                                                <div className={classes.dropdownList}>
                                                    {createLoading.vendors ? (
                                                        <div className={classes.dropdownItem}>Loading vendors...</div>
                                                    ) : filterItems('vendor').length === 0 ? (
                                                        <div className={classes.dropdownItem}>No vendors found</div>
                                                    ) : (
                                                        filterItems('vendor').map((item) => (
                                                            <div
                                                                key={item.id}
                                                                className={`${classes.dropdownItem} ${createFormData.vendor_id === item.id ? classes.selectedItem : ''}`}
                                                                onClick={() => handleSelect('vendor', item)}
                                                            >
                                                                {item.name || item.vendor_name}
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Next Service Date (Optional)</label>
                                    <input
                                        type="date"
                                        name="next_service_date"
                                        value={createFormData.next_service_date}
                                        onChange={handleCreateChange}
                                        min={createFormData.service_date}
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                className={classes.saveBtn}
                                disabled={submitting}
                            >
                                {submitting ? "Recording..." : "Record Maintenance"}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default AssetMaintenance;